/**
 * Fetches a Binary resource from a FHIR server and converts it into a Blob.
 * Handles both base64-encoded and raw binary formats.
 * @param {string} binaryRef - FHIR reference to the Binary resource (e.g. "Binary/123")
 * @param {object} fhirClient - Initialized SMART on FHIR client used to make requests
 * @returns {Promise<{ blob: Blob|null, deleted: boolean, reason?: string }>}
 */
async function fetchBinaryBlob(binaryRef, fhirClient) {
  console.log("Fetching binary blob for:", binaryRef);

  try {
    const binary = await fhirClient.request(binaryRef);

    // Case 1: Inline base64-encoded Binary (with `data`)
    if (binary.data) {
      try {
        const byteCharacters = atob(binary.data);
        const byteArray = new Uint8Array(
          [...byteCharacters].map((c) => c.charCodeAt(0))
        );
        const blob = new Blob([byteArray], { type: binary.contentType });

        return { blob, deleted: false };
      } catch (decodeError) {
        console.warn("Base64 decoding failed:", decodeError);
        throw new Error("Invalid base64 in Binary.data");
      }
    }

    // Case 2: Raw Binary resource — make direct request
    const response = await fetch(`${fhirClient.state.serverUrl}/${binaryRef}`, {
      headers: {
        Accept: binary.contentType || "application/octet-stream"
      }
    });

    if (!response.ok) {
      let errorBody;
      try {
        errorBody = await response.json();
      } catch (parseErr) {
        console.warn("Failed to parse error body:", parseErr);
      }

      const diagnosticsText =
        errorBody?.resourceType === "OperationOutcome"
          ? errorBody.issue?.[0]?.diagnostics
          : undefined;

      return {
        blob: null,
        deleted: response.status === 410 || errorBody?.resourceType === "OperationOutcome",
        reason:
          diagnosticsText ||
          `Attachment fetch failed with status ${response.status}.`
      };
    }

    const blob = await response.blob();
    return { blob, deleted: false };
  } catch (error) {
    console.error("Error fetching binary:", error);

    // ✅ Fallback: try to fetch manually to recover diagnostics
    try {
      const fallbackResponse = await fetch(`${fhirClient.state.serverUrl}/${binaryRef}`, {
        headers: { Accept: "application/json" }
      });

      let fallbackBody;
      try {
        fallbackBody = await fallbackResponse.json();
      } catch (parseErr) {
        console.warn("Fallback parse failed:", parseErr);
      }

      const diagnosticsText =
        fallbackBody?.resourceType === "OperationOutcome"
          ? fallbackBody.issue?.[0]?.diagnostics
          : undefined;

      return {
        blob: null,
        deleted: fallbackResponse.status === 410 || fallbackBody?.resourceType === "OperationOutcome",
        reason:
          diagnosticsText ||
          `Attachment fetch failed with status ${fallbackResponse.status}.`
      };
    } catch (fallbackError) {
      console.error("Fallback fetch failed:", fallbackError);
      return {
        blob: null,
        deleted: true,
        reason: "Attachment fetch failed. This Binary resource may have expired or been removed from the FHIR server."
      };
    }
  }
}

module.exports = { fetchBinaryBlob };